import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Link } from 'react-router-dom';
import { Anchor, Download, Ship, Undo2, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../components/ui/use-toast'; // Importar la función toast
import * as XLSX from 'xlsx-js-style'; // Importar la biblioteca xlsx con estilos
import { format, parseISO, isSameDay } from 'date-fns';

const Movimientos = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
   const [movimientos, setMovimientos] = useState([]);
   const [embarcacionesFuera, setEmbarcacionesFuera] = useState([]);
   const [isTableCleared, setIsTableCleared] = useState(false);

useEffect(() => {
  const fetchData = async () => {
    try {
      const response = await api.get('/movimientos');
      const data = response.data;

      const hoy = new Date();

      // 1. Mostrar SIEMPRE los que están en el agua
      const enElAgua = data.filter(mov =>
        mov.estado === 'En el agua'
      );

      // 2. Mostrar SOLO los que están fuera del agua HOY
      const fueraDelAguaHoy = data.filter(mov =>
        mov.estado === 'Fuera del agua' &&
        mov.fechaHoraSalida &&
        isSameDay(new Date(mov.fechaHoraSalida), hoy)
      );

      setMovimientos(enElAgua);
      setEmbarcacionesFuera(fueraDelAguaHoy);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  fetchData();
}, [isTableCleared]);

  const adjustTime = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) { // Verifica si la fecha es inválida
      return 'Fecha inválida'; // O maneja el error como prefieras
    }
    date.setHours(date.getHours() - 3); // Ajustar la diferencia horaria
    return date.toISOString().slice(0, 19).replace('T', ' ');
  };
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleDeshacer = async (movimientoId) => {
    try {
      await api.put(`/movimientos/${movimientoId}/deshacer`);
      toast({
        title: 'Deshacer realizado',
        description: 'La embarcación ha sido sacada del agua.',
      });

      // Actualiza las listas
      setMovimientos((prev) => prev.filter((mov) => mov.id !== movimientoId));
    } catch (error) {
      console.error('Error al deshacer:', error);
      toast({
        title: 'Error',
        description: 'No se pudo deshacer la acción.',
        variant: 'destructive',
      });
    }
  };

  const handleRegistrarSalida = async (movimientoId) => {
    try {
      const response = await api.post(`/movimientos/${movimientoId}/salida`);
      toast({
        title: 'Salida registrada',
        description: 'La embarcación ha sido registrada como fuera del agua.',
      });

      // Actualiza las listas
      setMovimientos((prev) => prev.filter((mov) => mov.id !== movimientoId));
      setEmbarcacionesFuera((prev) => [...prev, response.data]);
    } catch (error) {
      console.error('Error registrando la salida:', error);
      toast({
        title: 'Error',
        description: 'No se pudo registrar la salida.',
        variant: 'destructive',
      });
    }
  };

  const handleRegresarAlAgua = async (movimientoId) => {
    try {
      const response = await api.put(`/movimientos/${movimientoId}/regresar`);
      toast({
        title: 'Regreso realizado',
        description: 'La embarcación ha sido regresada al agua.',
      });

      // Actualiza las listas
      setEmbarcacionesFuera((prev) => prev.filter((mov) => mov.id !== movimientoId));
      setMovimientos((prev) => [...prev, response.data]);
    } catch (error) {
      console.error('Error al regresar al agua:', error);
      toast({
        title: 'Error',
        description: 'No se pudo regresar la embarcación al agua.',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async () => {
    const columns = [
      { header: 'Matrícula', key: 'numeroMatricula' },
      { header: 'Seguro', key: 'seguro' },
      { header: 'Vencimiento Seguro', key: 'vencimientoSeguro' },
      { header: 'Vencimiento Matrícula', key: 'vencimientoMatricula' },
      { header: 'Tipo de Embarcación', key: 'tipoEmbarcacion' },
      { header: 'Nombre de Fantasía', key: 'nombreFantasia' },
      { header: 'HP', key: 'hp' },
      { header: 'Capacidad', key: 'capacidad' },
      { header: 'Color Casco', key: 'colorCasco' },
      { header: 'Cliente', key: 'nombreCliente' },
      { header: 'DNI', key: 'dniCliente' },
      { header: 'Teléfono 1', key: 'telefono1' },
      { header: 'Teléfono 2', key: 'telefono2' },
      { header: 'Vencimiento Licencia', key: 'vencimientoLicencia' },
      { header: 'Marca Vehículo', key: 'marcaVehiculo' },
      { header: 'Patente Vehículo', key: 'patenteVehiculo' },
      { header: 'Color Vehículo', key: 'colorVehiculo' },
      { header: 'Salida registrada', key: 'fechaHoraSalida' },
    ];

    const data = embarcacionesFuera.map((movimiento) => ({
      numeroMatricula: movimiento.embarcacion?.numeroMatricula || 'N/A',
      seguro: movimiento.embarcacion?.seguro || 'N/A',
      vencimientoSeguro: movimiento.embarcacion?.vencimientoSeguro || 'N/A',
      vencimientoMatricula: movimiento.embarcacion?.vencimientoMatricula ?? '',
      tipoEmbarcacion: movimiento.embarcacion?.tipoEmbarcacion || 'N/A',
      nombreFantasia: movimiento.embarcacion?.nombreFantasia || 'N/A',
      hp: movimiento.embarcacion?.hp || 'N/A',
      capacidad: movimiento.embarcacion?.capacidad || 'N/A',
      colorCasco: movimiento.embarcacion?.colorCasco || 'N/A',
      nombreCliente: `${movimiento.cliente?.nombre || 'N/A'} ${movimiento.cliente?.apellido || ''}`.trim(),
      dniCliente: movimiento.cliente?.dni || 'N/A',
      telefono1: movimiento.cliente?.telefono1 || 'N/A',
      telefono2: movimiento.cliente?.telefono2 || 'N/A',
      vencimientoLicencia: movimiento.cliente?.vencimientoLicencia || 'N/A',
      marcaVehiculo: movimiento.vehiculo?.marca || 'N/A',
      patenteVehiculo: movimiento.vehiculo?.patente || 'N/A',
      colorVehiculo: movimiento.vehiculo?.color || 'N/A',
      fechaHoraSalida: adjustTime(movimiento.fechaHoraSalida) || 'N/A',
    }));

    const worksheetData = [
      columns.map((column) => column.header),
      ...data.map((row) => columns.map((column) => row[column.key] ?? 'N/A')),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' }, sz: 12 },
      fill: { patternType: 'solid', fgColor: { rgb: '1F4E79' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FFBFBFBF' } },
        right: { style: 'thin', color: { rgb: 'FFBFBFBF' } },
        bottom: { style: 'thin', color: { rgb: 'FFBFBFBF' } },
        left: { style: 'thin', color: { rgb: 'FFBFBFBF' } },
      },
    };

    const baseRowStyle = {
      font: { color: { rgb: 'FF1F2933' }, sz: 11 },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FFE1E5EA' } },
        right: { style: 'thin', color: { rgb: 'FFE1E5EA' } },
        bottom: { style: 'thin', color: { rgb: 'FFE1E5EA' } },
        left: { style: 'thin', color: { rgb: 'FFE1E5EA' } },
      },
    };

    const zebraStyles = [
      { ...baseRowStyle, fill: { patternType: 'solid', fgColor: { rgb: 'FFF7FAFC' } } },
      { ...baseRowStyle, fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFFFF' } } },
    ];

    columns.forEach((_, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
      const cell = worksheet[cellAddress];
      if (cell) {
        cell.s = headerStyle;
      }
    });

    for (let rowIndex = 1; rowIndex < worksheetData.length; rowIndex += 1) {
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
        const cell = worksheet[cellAddress];
        if (cell) {
          cell.s = zebraStyles[rowIndex % 2];
        }
      }
    }

    const columnWidths = columns.map((column) => {
      const maxContentLength = Math.max(
        column.header.length,
        ...data.map((row) => (row[column.key] ? String(row[column.key]).length : 0))
      );
      return { wch: Math.min(Math.max(maxContentLength + 2, 14), 45) };
    });

    worksheet['!cols'] = columnWidths;
    worksheet['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: worksheetData.length - 1, c: columns.length - 1 },
      }),
    };
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Embarcaciones Fuera del Agua');
    XLSX.writeFile(workbook, 'embarcaciones_fuera_del_agua.xlsx');
  
    // Prepare data for HistoricoMovimiento
    const historicoMovimientoData = embarcacionesFuera.map((movimiento) => ({
      fechaHoraIngreso: movimiento.fechaHoraIngreso,
      fechaHoraSalida: movimiento.fechaHoraSalida,
      embarcacion_id: movimiento.embarcacion_id,
      cliente_id: movimiento.cliente_id,
      vehiculo_id: movimiento.vehiculo_id,
      tipoMovimiento: movimiento.tipo_movimiento,
      observaciones: movimiento.observaciones,
      estado: movimiento.estado,
      embarcacion_numeroMatricula: movimiento.embarcacion.numeroMatricula,
      embarcacion_seguro: movimiento.embarcacion.seguro,
      embarcacion_vencimientoSeguro: movimiento.embarcacion.vencimientoSeguro,
      embarcacion_vencimientoMatricula: movimiento.embarcacion.vencimientoMatricula,
      embarcacion_tipoEmbarcacion: movimiento.embarcacion.tipoEmbarcacion,
      embarcacion_nombreFantasia: movimiento.embarcacion.nombreFantasia,
      embarcacion_hp: movimiento.embarcacion.hp,
      embarcacion_capacidad: movimiento.embarcacion.capacidad,
      embarcacion_colorCasco: movimiento.embarcacion.colorCasco,
      cliente_nombre: movimiento.cliente.nombre,
      cliente_apellido: movimiento.cliente.apellido,
      cliente_dni: movimiento.cliente.dni,
      cliente_telefono1: movimiento.cliente.telefono1,
      cliente_telefono2: movimiento.cliente.telefono2,
      cliente_vencimientoLicencia: movimiento.cliente.vencimientoLicencia,
      vehiculo_marca: movimiento.vehiculo.marca,
      vehiculo_patente: movimiento.vehiculo.patente,
      vehiculo_color: movimiento.vehiculo.color
    }));

    try {
      // Fetch existing historical movements
      const existingHistoricoMovimientos = await api.get('/historico-movimientos');
      const existingData = existingHistoricoMovimientos.data;

      // Filter out movements that already exist in the historical movements table
      const newHistoricoMovimientoData = historicoMovimientoData.filter((movimiento) => {
        return !existingData.some((existing) => 
          existing.embarcacion_numeroMatricula === movimiento.embarcacion_numeroMatricula &&
          existing.cliente_id === movimiento.cliente_id &&
          format(parseISO(existing.fechaHoraIngreso), 'yyyy-MM-dd') === format(parseISO(movimiento.fechaHoraIngreso), 'yyyy-MM-dd')
        );
      });

      // Save new historical movements
      if (newHistoricoMovimientoData.length > 0) {
        await api.post('/historico-movimientos', { historicoMovimientoData: newHistoricoMovimientoData });
        toast({
          title: 'Datos guardados',
          description: 'La información ha sido guardada en el histórico de movimientos.',
        });
      } else {
        toast({
          title: 'Sin nuevos datos',
          description: 'No hay nuevos movimientos para guardar en el histórico.',
        });
      }
    } catch (error) {
      console.error('Error guardando en el histórico de movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar la información en el histórico de movimientos.',
        variant: 'destructive',
      });
    }
  };

  const handleClearTable = async () => {
  if (window.confirm('¿Está seguro de que desea archivar y limpiar los movimientos fuera del agua?')) {
    try {
      await api.post('/movimientos/archivar');
      toast({
        title: 'Tabla limpiada',
        description: 'Se archivaron y eliminaron los movimientos fuera del agua.',
      });
      setIsTableCleared(true); // Esto dispara el useEffect y actualiza la tabla
    } catch (error) {
      console.error('Error al archivar movimientos:', error);
      toast({
        title: 'Error',
        description: 'Ocurrió un problema al archivar los movimientos.',
        variant: 'destructive',
      });
    }
  }
};


  const filteredMovimientos = movimientos.filter(movimiento => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (movimiento.id.toString().includes(searchTermLower)) ||
      (movimiento.embarcacion?.numeroMatricula?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.cliente?.id.toString().includes(searchTermLower) || '') ||
      (movimiento.cliente?.nombre?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.cliente?.apellido?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.muelle?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.embarcacion?.nombreFantasia?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.embarcacion?.tipoEmbarcacion?.toLowerCase().includes(searchTermLower) || '')
    );
  });
  
  const filteredEmbarcacionesFuera = embarcacionesFuera.filter(movimiento => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (movimiento.id.toString().includes(searchTermLower)) ||
      (movimiento.embarcacion?.numeroMatricula?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.cliente?.id.toString().includes(searchTermLower) || '') ||
      (movimiento.cliente?.nombre?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.cliente?.apellido?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.embarcacion?.nombreFantasia?.toLowerCase().includes(searchTermLower) || '') ||
      (movimiento.embarcacion?.tipoEmbarcacion?.toLowerCase().includes(searchTermLower) || '')
    );
  });
  const sortedMovimientos = [...filteredMovimientos].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const sortedEmbarcacionesFuera = [...filteredEmbarcacionesFuera].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date) ? 'Fecha inválida' : format(date, 'dd-MM-yyyy');
  };

  const isDateExpired = (dateString) => {
    if (!dateString) return false;

    const date = new Date(dateString);
    if (isNaN(date)) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getExpirationCellClassName = (dateString) => (
    isDateExpired(dateString) ? 'text-red-600 font-semibold' : undefined
  );

  return (
    <div className="min-h-screen bg-gradient-to-b text-white">
      <Navbar />

      <main className="container mx-auto mt-8 px-4">
        <h1 className="text-4xl text-azul-oscuro font-bold mb-8">Movimientos de Embarcaciones</h1>

        <div className="mb-8">
          <Input
            type="text"
            placeholder="Buscar por cliente id, matrícula, cliente, embarcacion, nombre fantasia, tipo embarcacion..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md"
          />
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Embarcaciones en el Agua</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Acciones</TableHead>
                  <TableHead>Cliente ID</TableHead> {/* New column for Cliente ID */}
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Seguro</TableHead>
                  <TableHead>Vencimiento Matrícula</TableHead>
                  <TableHead>Vencimiento Seguro</TableHead>
                  <TableHead>Tipo de Embarcación</TableHead>
                  <TableHead>Nombre Fantasía</TableHead>
                  <TableHead>HP</TableHead>
                  <TableHead>Capacidad</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>DNI</TableHead>
                  <TableHead>Teléfono 1</TableHead>
                  <TableHead>Teléfono 2</TableHead>
                  <TableHead>Vencimiento Licencia</TableHead>
                  <TableHead>Vehículo</TableHead>
                  <TableHead>Patente</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Fecha y Hora de Ingreso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMovimientos.map((movimiento) => (
                  <TableRow key={movimiento.id}>
                    <TableCell>
                      <Button
                        type="yellow-gold"
                        size="sm"
                        onClick={() => handleDeshacer(movimiento.id)}
                        className="mr-2"
                      >
                        <Undo2 className="h-4 w-4" /> Deshacer
                      </Button>
                      <Button
                        type="yellow-gold"
                        size="sm"
                        onClick={() => handleRegistrarSalida(movimiento.id)}
                      >
                        <Anchor className="h-4 w-4" /> Registrar Salida
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/detallesclientes/${movimiento.cliente?.id}`}
                        className="text-azul-oscuro hover:underline font-medium"
                      >
                        {movimiento.cliente?.id || 'N/A'}
                      </Link>
                    </TableCell>
                    <TableCell>{movimiento.embarcacion?.numeroMatricula || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.seguro || 'N/A'}</TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.embarcacion?.vencimientoMatricula)}>
                      {formatDate(movimiento.embarcacion?.vencimientoMatricula)}
                    </TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.embarcacion?.vencimientoSeguro)}>
                      {formatDate(movimiento.embarcacion?.vencimientoSeguro)}
                    </TableCell>
                    <TableCell>{movimiento.embarcacion?.tipoEmbarcacion || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.nombreFantasia || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.hp || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.capacidad || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.colorCasco || 'N/A'}</TableCell>
                    <TableCell>
                      <Link
                        to={`/detallesclientes/${movimiento.cliente?.id}`}
                        className="text-azul-oscuro hover:underline font-medium"
                      >
                        {movimiento.cliente?.nombre || 'N/A'} {movimiento.cliente?.apellido || ''}
                      </Link>
                    </TableCell>
                    <TableCell>{movimiento.cliente?.dni || 'N/A'}</TableCell>
                    <TableCell>{movimiento.cliente?.telefono1 || 'N/A'}</TableCell>
                    <TableCell>{movimiento.cliente?.telefono2 || 'N/A'}</TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.cliente?.vencimientoLicencia)}>
                      {formatDate(movimiento.cliente?.vencimientoLicencia)}
                    </TableCell>
                    <TableCell>{movimiento.vehiculo?.marca || 'N/A'}</TableCell>
                    <TableCell>{movimiento.vehiculo?.patente || 'N/A'}</TableCell>
                    <TableCell>{movimiento.vehiculo?.color || 'N/A'}</TableCell>
                    <TableCell>{adjustTime(movimiento.fechaHoraIngreso) || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Embarcaciones Fuera del Agua</CardTitle>
            <div className="flex space-x-2">
              <Button onClick={handleDownload} className="ml-auto">
                <Download className="mr-2 h-4 w-4" /> Descargar Tabla
              </Button>
              <Button onClick={handleClearTable} className="ml-auto">
                <X className="mr-2 h-4 w-4" /> Limpiar Tabla
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Acciones</TableHead>
                  <TableHead>Cliente ID</TableHead> {/* New column for Cliente ID */}
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Seguro</TableHead>
                  <TableHead>Vencimiento Matrícula</TableHead>
                  <TableHead>Vencimiento Seguro</TableHead>
                  <TableHead>Tipo de Embarcación</TableHead>
                  <TableHead>Nombre Fantasía</TableHead>
                  <TableHead>HP</TableHead>
                  <TableHead>Capacidad</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>DNI</TableHead>
                  <TableHead>Teléfono 1</TableHead>
                  <TableHead>Teléfono 2</TableHead>
                  <TableHead>Vencimiento Licencia</TableHead>
                  <TableHead>Vehículo</TableHead>
                  <TableHead>Patente</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Fecha y Hora de Salida</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedEmbarcacionesFuera.map((movimiento) => (
                  <TableRow key={movimiento.id}>
                    <TableCell>
                      <Button
                        type="yellow-gold"
                        size="sm"
                        onClick={() => handleRegresarAlAgua(movimiento.id)}
                      >
                        <Ship className="h-4 w-4" /> Regresar al Agua
                      </Button>
                    </TableCell>
                    <TableCell>{movimiento.cliente?.id || 'N/A'}</TableCell> {/* New cell for Cliente ID */}
                    <TableCell>{movimiento.embarcacion?.numeroMatricula || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.seguro || 'N/A'}</TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.embarcacion?.vencimientoMatricula)}>
                      {formatDate(movimiento.embarcacion?.vencimientoMatricula)}
                    </TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.embarcacion?.vencimientoSeguro)}>
                      {formatDate(movimiento.embarcacion?.vencimientoSeguro)}
                    </TableCell>
                    <TableCell>{movimiento.embarcacion?.tipoEmbarcacion || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.nombreFantasia || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.hp || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.capacidad || 'N/A'}</TableCell>
                    <TableCell>{movimiento.embarcacion?.colorCasco || 'N/A'}</TableCell>
                    <TableCell>{movimiento.cliente?.nombre || 'N/A'} {movimiento.cliente?.apellido || ''}</TableCell>
                    <TableCell>{movimiento.cliente?.dni || 'N/A'}</TableCell>
                    <TableCell>{movimiento.cliente?.telefono1 || 'N/A'}</TableCell>
                    <TableCell>{movimiento.cliente?.telefono2 || 'N/A'}</TableCell>
                    <TableCell className={getExpirationCellClassName(movimiento.cliente?.vencimientoLicencia)}>
                      {formatDate(movimiento.cliente?.vencimientoLicencia)}
                    </TableCell>
                    <TableCell>{movimiento.vehiculo?.marca || 'N/A'}</TableCell>
                    <TableCell>{movimiento.vehiculo?.patente || 'N/A'}</TableCell>
                    <TableCell>{movimiento.vehiculo?.color || 'N/A'}</TableCell>
                    <TableCell>{adjustTime(movimiento.fechaHoraSalida) || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Movimientos;
